import { useMemo, type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import moment from "moment-timezone";
import { Outlet } from "react-router-dom";
import { Formik, yupToFormErrors, type FormikErrors, type FormikProps, type FormikTouched } from "formik";

import { Spinner } from "components/Shared";
import { CICard } from "components/CriticalIncident/components/Shared/CICard/CICard";
import { PriorityAlert } from "components/Shared/PriorityAlert";
import { IncidentReportStatusChip } from "./IncidentReportStatusChip";
import useTimezoneUtils from "hooks/useTimezoneUtils";
import { ReportNavigation, ReportNavigationBlocker, useReportNavigation } from "./ReportNavigation";
import { Button } from "components/Shared/Inputs";
import { useIncidentReportContext, type ReportNavigationKey } from "./IncidentReportContextProvider";
import { IncidentReportStatus, type IncidentReport } from "components/CriticalIncident/interfaces";
import { useSubmitIncidentReport, useUpdateIncidentReport } from "api/queries/incidentReportQuery";
import type { IncidentReportUpdatePayload } from "api/types";
import { LoadingOverlay } from "components/Shared/LoadingOverlay";
import { getValidationSchema } from "./schema";

import "./IncidentReportTab.scss";

// Only these AU sections have a live v2 backend; every other section save (and every
// non-AU provider's save, regardless of section) stays on v1.
const V2_UPDATE_SECTIONS: ReportNavigationKey[] = ['individualSupportSession', 'referringManager', 'deliveryHours'];

export const IncidentReportTab: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { report: incidentReport, remoteReport, isLoading, updateErrors, errorSections, affirmations, hasAttemptedSubmit, setSubmitAttempted, isAU } = useIncidentReportContext();
  const validationSchema = useMemo(() => getValidationSchema(isAU), [isAU]);
  const { mutateAsync: updateIncidentReportV1, isPending: isPendingV1 } = useUpdateIncidentReport(undefined, 'v1');
  const { mutateAsync: updateIncidentReportV2, isPending: isPendingV2 } = useUpdateIncidentReport(undefined, 'v2');
  const isPending = isPendingV1 || isPendingV2;
  const { mutateAsync: submitIncidentReport, isPending: isSubmitting } = useSubmitIncidentReport(undefined, isAU ? 'v2' : 'v1');
  const { currentSection, allMenus, isLastSection, activeSectionNames, previousSection, nextSection, navigateToSection, exitReport, toggleMenuVisibility } = useReportNavigation();
  const { localeTimeFormat } = useTimezoneUtils();
  const isReadonly = useMemo(() => incidentReport?.status === IncidentReportStatus.Submitted, [incidentReport?.status]);

  const onSubmit = async (values: IncidentReport) => {
    // formik.validateForm(formik.values);
    console.info('🎯➡️ incident report data:', values);
    if (currentSection === 'review') {
      await submitIncidentReport({ caseId: incidentReport?.caseId!, activityId: incidentReport?.activityId!, id: incidentReport?.id!, payload: values });
      navigateToSection('interventionSummary');
      toggleMenuVisibility('review', true);
    }
  };

  // Recursive helper to mark all keys as true
  const createTouchedObject = (values: unknown): unknown => {
    if (Array.isArray(values)) {
      return values.map(item => createTouchedObject(item));
    }

    if (values !== null && typeof values === 'object') {
      return Object.keys(values).reduce((acc, key) => {
        acc[key] = createTouchedObject((values as Record<string, unknown>)[key]);
        return acc;
      }, {} as Record<string, unknown>);
    }

    return true;
  };

  const touchAllFields = (formik: FormikProps<IncidentReport>) => {
    const allTouched = createTouchedObject(formik.values) as FormikTouched<IncidentReport>;
    formik.setTouched(allTouched, false);
  };

  // Formik's `touched`/`errors` are wiped back to {} whenever `enableReinitialize` resets the
  // form (which happens every time a save's query invalidation refetches a changed remoteReport).
  // Deriving initialTouched/initialErrors from remoteReport means that reset re-applies the
  // already-attempted validation state instead of clearing it out from under the user.
  const initialTouched = useMemo(
    () => (hasAttemptedSubmit && remoteReport ? (createTouchedObject(remoteReport) as FormikTouched<IncidentReport>) : {}),
    [hasAttemptedSubmit, remoteReport]
  );

  const initialErrors = useMemo<FormikErrors<IncidentReport>>(() => {
    if (!hasAttemptedSubmit || !remoteReport) return {};
    try {
      validationSchema.validateSync(remoteReport, { abortEarly: false });
      return {};
    } catch (err) {
      return yupToFormErrors(err);
    }
  }, [hasAttemptedSubmit, remoteReport, validationSchema]);

  const runValidation = async (formik: FormikProps<IncidentReport>) => {
    touchAllFields(formik);
    const errors = await formik.validateForm();
    const sectionErrors: Set<ReportNavigationKey> = new Set<ReportNavigationKey>();
    const validKeys = allMenus.map(mnu => mnu.labelKey);
    Object.keys(errors).forEach(sectn => {
      if (validKeys.includes(sectn as ReportNavigationKey)) {
        sectionErrors.add(sectn as ReportNavigationKey)
      }
    });
    updateErrors(Array.from(sectionErrors));
    return errors;
  };

  const validate = async (formik: FormikProps<IncidentReport>) => {
    setSubmitAttempted(true);
    const errors = await runValidation(formik);
    console.info('💥💣🤯 Error submitting!', errors);
  }

  const buildPayload = (report: IncidentReport): IncidentReportUpdatePayload => {
    return {
      section: currentSection,
      ...report[currentSection]
    } as IncidentReportUpdatePayload;
  };

  const saveIncidentReport = async (formik: FormikProps<IncidentReport>) => {
    const { caseId, activityId, id = '' } = incidentReport || {};
    if (!(activityId && caseId)) {
      console.error('🐞 you need caseId and activityId to save the report');
      return;
    }
    const updateIncidentReport = isAU && V2_UPDATE_SECTIONS.includes(currentSection) ? updateIncidentReportV2 : updateIncidentReportV1;
    await updateIncidentReport({ activityId, caseId, id, payload: buildPayload({ ...incidentReport, ...formik.values }) });
    if (hasAttemptedSubmit) {
      await runValidation(formik);
    } else {
      updateErrors([]);
    }
  };

  const saveIfTouched = async (formik: FormikProps<IncidentReport>) => {
    if (formik.dirty && !isReadonly) {
      await saveIncidentReport(formik);
    }
  };

  const handleBack = async (formik: FormikProps<IncidentReport>) => {
    await saveIfTouched(formik);
    previousSection();
  };

  const handleContinue = async (formik: FormikProps<IncidentReport>) => {
    if (
      isLastSection
      && !isReadonly
      && currentSection !== 'review'
      && !activeSectionNames.includes('review')
    ) {
      toggleMenuVisibility('review', false);
      await saveIfTouched(formik);
      navigateToSection('review');
      return;
    }
    await saveIfTouched(formik);
    nextSection();
  };

  const handleSaveAndExit = async (formik: FormikProps<IncidentReport>) => {
    await saveIncidentReport(formik);
    exitReport();
  };

  const statusDateStr = isReadonly ? incidentReport?.submittedOn : incidentReport?.modifiedOn;


  return translationsReady && incidentReport ? (
    <Formik
      initialValues={remoteReport || {} as IncidentReport}
      initialTouched={initialTouched}
      initialErrors={initialErrors}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {(formik) => (
        <Container className="incident_report_container">
          <CICard className="incident_report_header case_summary_paper">
            <div className="incident_report_title_status">
              <h2>{t('incident_report')}</h2>
              <div className="status_last_saved">
                <IncidentReportStatusChip status={incidentReport?.status} hasError={!!errorSections.length} />
                <div>{t(isReadonly ? 'submitted' : 'last_saved')}: {statusDateStr ? moment(statusDateStr).format(`MMMM Do, YYYY | ${localeTimeFormat}`) : t('never')}</div>
              </div>
            </div>
            {isReadonly && (<p className="incident_report_submit_success" dangerouslySetInnerHTML={{ __html: t('successfully_submitted') }} />)}
            <PriorityAlert priority="normal">
              <div dangerouslySetInnerHTML={{ __html: t(isReadonly ? 'incident_report_post_submit_instructions' : isAU && currentSection === 'review' ? 'incident_report_review_instructions' : 'incident_report_instructions')}} />
            </PriorityAlert>
            {isReadonly && <Button LinkComponent={"a"} variant="outline" href={t('ci_addendum_form_url')} target="_blank" rel="noopener noreferrer">{t('download_template')}</Button>}
          </CICard>
          <Grid container spacing={2} className="incident_report_main">
            <LoadingOverlay show={(isLoading || isPending || isSubmitting)} />
            <Grid item xs={12} md={9}>
              <CICard className="incident_report_form">
                <form onSubmit={formik.handleSubmit} noValidate>
                  <Outlet context={{ isReadonly }} />
                  <footer className="incident_report_actions">
                    <div className="left_buttons">{ currentSection !== 'interventionSummary' && <Button variant="outline" onClick={() => handleBack(formik)}>{t('back')}</Button>}</div>
                    <div className="right_buttons">
                      {!isReadonly && (<Button variant="text" onClick={() => handleSaveAndExit(formik)}>{t('save_and_exit')}</Button>)}
                      {currentSection === 'review' ? (
                        <Button type="submit" onClick={() => validate(formik)} disabled={!(affirmations.submissionIsCorrect && affirmations.timesheetSent && (!isAU || affirmations.followUpsFinalised)) || isReadonly}>{t('submit')}</Button>
                      ) : !(isLastSection && isReadonly) && (
                        <Button onClick={() => handleContinue(formik)}>{t('continue')}</Button>
                      )}
                    </div>
                  </footer>
                </form>
              </CICard>
            </Grid>
            <Grid item xs={0} md={3}>
              <ReportNavigation onBeforeNavigate={() => saveIfTouched(formik)} />
              <ReportNavigationBlocker formik={formik} onSave={() => saveIfTouched(formik)} />
            </Grid>
          </Grid>
        </Container>
      )}
    </Formik>
  ) : <Spinner fillParent />;
};
